// B-154 — "Transacciones y reversas", in Reportes: Banco Agrario's own dashboard (the operator's pivot
// table over its transactions workbook and the consolidated reversals) since January 2024. The backend
// joins the history loaded from the operator's workbook with the bank's daily file the closings read,
// never counting a day twice (`getCierreTransactionsDashboard`); this draws it: the period's figures,
// transactions and value by month (by day for a month), by transaction, regional and office, and the
// reversals — by month, motive, attribution, cashier, and per 1.000 transactions.
//
// Drawn only for a client with the dashboard: for any other the route answers 404 and nothing is shown.
// The pure half is `utils/cierreTransacciones.js`.
//
// B-161 — what the operator's own dashboard (Yeni's) also draws: a chip per year and a regional filter,
// the month of the period's end against the month before (day by day, daily and accumulated variation)
// and its year against the year before (month by month), in volume or in value. By day, a weekend or a
// holiday is a blank day, never skipped.
//
// B-171 — by department and municipality too, as Yeni's template slices them: two selects (the
// municipalities narrowed by the department) and two tables, the municipality named from DIVIPOLA.
//
// Drawn with `CierresVisual.jsx`: figures sized to fit their card and said in words when large, tables
// that scroll inside their card, charts that say when there is nothing to draw.
import { useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ArrowLeftRight, Download, Receipt, RotateCcw, Undo2,
} from "lucide-react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import { getCierreTransactionsDashboard } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, montoLegible } from "../../../utils/cierrePanel";
import { formatMoney } from "../../../utils/formatMoney";
import { descargarTablas } from "../../../utils/excelTabla";
import { ejeCorto } from "../../../utils/cierreCifras";
import {
  PERIODOS, acumuladoAlCorte, aniosDelTablero, filasDeComparacion, hojasDeTransacciones, mesEnPalabras, municipiosDe,
  nombreDeMunicipio, puntosDeSerie, rangoDePeriodo, tasaEnPalabras, variacionEnPalabras,
} from "../../../utils/cierreTransacciones";
import {
  BarraDeFiltros, Barras, Cifra, Filtro, LugarDeGrafica, SELECT_DE_FILTRO, Segmentado, Tabla, Tarjeta, celda, enPalabras, fila,
  leyendaLegible, useEstrecho,
} from "./CierresVisual";

const SERIE = ["hsl(var(--primary))", "#3b5ba9", "#6f86c2", "#93a3c4", "#c7d0e2", "#e2e8f0"];
const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
// The legend's height is measured by Recharts, never fixed: on a phone it takes three lines.
const LEYENDA = {
  verticalAlign: "top", align: "left", wrapperStyle: { fontSize: 11.5, lineHeight: "16px", paddingBottom: 6 }, formatter: leyendaLegible,
};
const EJE = {
  tickLine: false, axisLine: false, fontSize: 11, tick: { fill: "var(--brinks-muted-foreground)" },
};
const tasa = (r) => (r === null || r === undefined ? "—" : String(r).replace(".", ","));
const tonoDeVariacion = (v) => (v === null || v === undefined ? "text-brinks-muted-foreground" : v < 0 ? "text-brinks-danger" : "text-brinks-success");

/**
 * B-161 — one comparison, as the operator's dashboard draws it: this period's bars beside the one
 * before, the accumulated variation as a line, and the table with the daily and the accumulated one.
 */
function Comparativo({
  titulo, actual, anterior, lineas, medida, etiqueta, nombreDeLinea,
}) {
  const filas = filasDeComparacion(lineas, medida, etiqueta);
  const estrecho = useEstrecho();
  const corte = acumuladoAlCorte(lineas);
  const formato = medida === "valor" ? montoLegible : formatMoney;
  const vacio = !filas.some((f) => f.actual !== null || f.anterior !== null);
  return (
    <Tarjeta
      titulo={titulo}
      subtitulo={corte
        ? `Acumulado al ${nombreDeLinea(corte.key)}: ${formato(corte.accumulated.current[medida])} contra ${formato(corte.accumulated.previous[medida])} · ${variacionEnPalabras(corte.accumulated.variation[medida])}`
        : filas.some((f) => f.anterior !== null)
          ? `${actual} todavía no tiene días con transacciones`
          : "Sin transacciones en ninguno de los dos periodos"}
    >
      <LugarDeGrafica vacio={vacio ? "Sin transacciones en estos periodos" : null} alto="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filas} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--brinks-border)" vertical={false} />
            <XAxis dataKey="etiqueta" {...EJE} interval="preserveStartEnd" minTickGap={8} />
            <YAxis yAxisId="cifra" {...EJE} tickFormatter={ejeCorto} width={estrecho ? 48 : 64} />
            <YAxis yAxisId="pct" orientation="right" {...EJE} tickFormatter={(v) => `${v}%`} width={44} hide={estrecho} />
            <Tooltip
              formatter={(v, nombre) => (v === null || v === undefined ? "—" : nombre === "Variación acumulada" ? variacionEnPalabras(v) : formato(v))}
              cursor={{ fill: "var(--brinks-surface)" }}
            />
            <Legend {...LEYENDA} />
            <Bar yAxisId="cifra" dataKey="anterior" name={anterior} fill={SERIE[4]} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            <Bar yAxisId="cifra" dataKey="actual" name={actual} fill={SERIE[0]} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            <Line yAxisId="pct" type="monotone" dataKey="variacionAcumulada" name="Variación acumulada" stroke="var(--brinks-warning)" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </LugarDeGrafica>
      <div className="mt-3">
        <Tabla
          etiqueta={`Tabla ${titulo}`}
          alto="max-h-[280px]"
          columnas={[{ titulo: "" }, { titulo: actual, numerica: true }, { titulo: anterior, numerica: true }, { titulo: "Variación", numerica: true }, { titulo: "Acumulada", numerica: true }]}
        >
          {filas.map((f) => (
            <tr key={f.clave} className={fila()}>
              <td className={celda({ principal: true })}>{f.etiqueta}</td>
              <td className={celda({ numerica: true })}>{f.actual === null ? "—" : formato(f.actual)}</td>
              <td className={celda({ numerica: true })}>{f.anterior === null ? "—" : formato(f.anterior)}</td>
              <td className={celda({ numerica: true, tono: tonoDeVariacion(f.variacion) })}>{variacionEnPalabras(f.variacion)}</td>
              <td className={celda({ numerica: true, tono: tonoDeVariacion(f.variacionAcumulada) })}>{variacionEnPalabras(f.variacionAcumulada)}</td>
            </tr>
          ))}
        </Tabla>
      </div>
    </Tarjeta>
  );
}

/** A table of places (regional, department, municipality): TX, value, reversals and the rate. */
function TablaDeLugares({ etiqueta, columna, filas, nombre }) {
  return (
    <Tabla
      etiqueta={etiqueta}
      columnas={[{ titulo: columna }, { titulo: "TX", numerica: true }, { titulo: "Valor", numerica: true }, { titulo: "Reversas", numerica: true }, { titulo: "Por 1.000", numerica: true }]}
      vacia={filas.length ? null : "Sin transacciones en el periodo"}
    >
      {filas.map((r) => {
        const { principal, detalle, clave } = nombre(r);
        return (
          <tr key={clave} className={fila()}>
            <td className={celda({ principal: true })}>
              {principal}
              {detalle ? <span className="block text-[11.5px] font-normal text-brinks-muted-foreground">{detalle}</span> : null}
            </td>
            <td className={celda({ numerica: true })}>{formatMoney(r.tx)}</td>
            <td className={celda({ numerica: true })}>{montoLegible(r.valor)}</td>
            <td className={celda({ numerica: true })}>{r.reversals || "—"}</td>
            <td className={celda({ numerica: true })}>{r.reversals ? tasa(r.rate) : "—"}</td>
          </tr>
        );
      })}
    </Tabla>
  );
}

/** B-159 — how many of the period's reversals the bank's file proposed and nobody completed yet. */
export const detalleDePendientes = (n) => (n ? `${n} por completar` : null);

export function TransaccionesReversas({ token, mantisBusinessId, clientCode, puedeExportar }) {
  const [periodo, setPeriodo] = useState("todo");
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(false);
  // B-161 — the regional chosen ("" is every one), the regionals offered, and the comparisons' measure.
  const [regional, setRegional] = useState("");
  const [regionales, setRegionales] = useState([]);
  const [medida, setMedida] = useState("tx");
  // B-171 — the department and municipality chosen ("" is every one), and the places offered.
  const [departamento, setDepartamento] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [lugares, setLugares] = useState({ departments: [], municipalities: [] });

  useEffect(() => {
    if (!clientCode) return undefined;
    let activo = true;
    setEstado("cargando");
    setError(null);
    const pedido = {
      ...rangoDePeriodo(periodo),
      ...(regional ? { regions: [regional] } : {}),
      ...(departamento ? { departments: [departamento] } : {}),
      ...(municipio ? { municipalities: [municipio] } : {}),
    };
    getCierreTransactionsDashboard(token, mantisBusinessId, clientCode, pedido)
      .then((res) => {
        if (!activo) return;
        if (res?.success) {
          setDatos(res.data);
          if (res.data?.regions) setRegionales(res.data.regions);
          if (res.data?.departments) setLugares({ departments: res.data.departments, municipalities: res.data.municipalities || [] });
          setEstado("listo");
        } else if (res?.errorCode === "NOT_FOUND" || res?.status === 404) {
          setEstado("sin");
        } else {
          setError(correctionError(res));
          setEstado("error");
        }
      })
      .catch(() => {
        if (!activo) return;
        setError("No se pudo cargar el tablero de transacciones.");
        setEstado("error");
      });
    return () => { activo = false; };
  }, [token, mantisBusinessId, clientCode, periodo, regional, departamento, municipio]);

  const puntos = useMemo(() => puntosDeSerie(datos), [datos]);
  const estrecho = useEstrecho();
  const principales = useMemo(() => (datos?.byConcept || []).slice(0, 5), [datos]);

  if (estado === "sin" || !clientCode) return null;

  const exportar = async () => {
    setExportando(true);
    try {
      await descargarTablas(`Transacciones y reversas ${clientCode} ${datos.period.from} a ${datos.period.to}.xlsx`, hojasDeTransacciones(datos));
    } finally {
      setExportando(false);
    }
  };

  const totales = datos?.totals;
  const porDia = datos?.granularity === "day";
  const sinTransacciones = !puntos.some((p) => p.tx);
  const sinReversas = !puntos.some((p) => p.reversas);

  return (
    <section className="min-w-0 space-y-3 rounded-[16px] border bg-brinks-surface p-3" aria-label="Transacciones y reversas">
      <header className="space-y-3 px-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-brinks-foreground">Transacciones y reversas</h2>
            <p className="mt-0.5 text-[12.5px] text-brinks-muted-foreground">
              Las transacciones del banco por oficina y las reversas de cada una
              {datos ? ` · del ${fechaDMY(datos.period.from)} al ${fechaDMY(datos.period.to)}` : ""}
            </p>
          </div>
          {puedeExportar && datos ? (
            <Button variant="outline" size="sm" onClick={exportar} disabled={exportando} className="h-8 shrink-0 gap-1.5">
              {exportando ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
              Exportar Excel
            </Button>
          ) : null}
        </div>
        <BarraDeFiltros
          periodo={(
            <Filtro etiqueta="Periodo">
              <Segmentado opciones={[...PERIODOS, ...aniosDelTablero(datos?.since)]} elegida={periodo} onElegir={setPeriodo} etiqueta="Periodo" />
            </Filtro>
          )}
        >
          {regionales.length > 1 || lugares.departments.length > 1 || lugares.municipalities.length > 1 ? (
            <div className="grid gap-2 sm:grid-cols-3">
              <Filtro etiqueta="Regional">
                <select aria-label="Regional" value={regional} onChange={(e) => setRegional(e.target.value)} className={SELECT_DE_FILTRO} disabled={regionales.length < 2}>
                  <option value="">Todas las regionales</option>
                  {regionales.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Filtro>
              <Filtro etiqueta="Departamento">
                <select
                  aria-label="Departamento"
                  value={departamento}
                  onChange={(e) => { setDepartamento(e.target.value); setMunicipio(""); }}
                  className={SELECT_DE_FILTRO}
                  disabled={lugares.departments.length < 2}
                >
                  <option value="">Todos los departamentos</option>
                  {lugares.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Filtro>
              <Filtro etiqueta="Municipio">
                <select aria-label="Municipio" value={municipio} onChange={(e) => setMunicipio(e.target.value)} className={SELECT_DE_FILTRO} disabled={lugares.municipalities.length < 2}>
                  <option value="">Todos los municipios</option>
                  {municipiosDe(lugares.municipalities, departamento).map((m) => <option key={m.dane} value={m.dane}>{m.nombre}</option>)}
                </select>
              </Filtro>
            </div>
          ) : null}
        </BarraDeFiltros>
      </header>

      {estado === "cargando" && !datos ? (
        <div className="grid h-40 place-items-center"><Spinner /></div>
      ) : null}
      {estado === "error" ? (
        <p className="rounded-[10px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12.5px] text-brinks-danger">{error}</p>
      ) : null}

      {datos ? (
        <div className={cn("space-y-3", estado === "cargando" && "opacity-60")}>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <Cifra
              icono={ArrowLeftRight}
              etiqueta="Transacciones"
              valor={formatMoney(totales.tx)}
              palabras={enPalabras(totales.tx)}
              detalle={`${totales.offices} oficinas`}
              destacada
            />
            <Cifra
              icono={Receipt}
              etiqueta="Valor transado"
              valor={montoLegible(totales.valor)}
              palabras={enPalabras(totales.valor, { pesos: true })}
              detalle="Suma de las transacciones del periodo"
            />
            <Cifra
              icono={Undo2}
              etiqueta="Reversas"
              valor={formatMoney(totales.reversals)}
              palabras={enPalabras(totales.reversalsValue, { pesos: true })}
              detalle={[montoLegible(totales.reversalsValue), detalleDePendientes(totales.reversalsPending)].filter(Boolean).join(" · ")}
            />
            <Cifra icono={RotateCcw} etiqueta="Tasa de reversas" valor={tasaEnPalabras(totales.rate)} detalle="Reversas por cada 1.000 transacciones" />
          </div>

          <Tarjeta
            titulo={porDia ? "Transacciones por día" : "Transacciones por mes"}
            subtitulo="Barras: transacciones por tipo (las 5 de más valor y el resto). Línea: el valor transado."
          >
            <LugarDeGrafica vacio={sinTransacciones ? "Sin transacciones en el periodo" : null} alto="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={puntos} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--brinks-border)" vertical={false} />
                  <XAxis dataKey="etiqueta" {...EJE} interval="preserveStartEnd" minTickGap={12} />
                  <YAxis yAxisId="tx" {...EJE} tickFormatter={ejeCorto} width={estrecho ? 44 : 58} />
                  <YAxis yAxisId="valor" orientation="right" {...EJE} tickFormatter={(v) => `$${ejeCorto(v)}`} width={78} hide={estrecho} />
                  <Tooltip formatter={(v, nombre) => (v === null || v === undefined ? "—" : nombre === "Valor" ? montoLegible(v) : formatMoney(v))} cursor={{ fill: "var(--brinks-surface)" }} />
                  <Legend {...LEYENDA} />
                  {principales.map((c, i) => (
                    <Bar key={c.concept} yAxisId="tx" dataKey={c.concept} name={c.label} stackId="tx" fill={SERIE[i]} isAnimationActive={false} />
                  ))}
                  <Bar yAxisId="tx" dataKey="otras" name="Otras" stackId="tx" fill={SERIE[5]} isAnimationActive={false} />
                  <Line yAxisId="valor" type="monotone" dataKey="valor" name="Valor" stroke="var(--brinks-warning)" strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </LugarDeGrafica>
          </Tarjeta>

          {datos.comparisons ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <h3 className="text-[14px] font-semibold text-brinks-foreground">Comparativos</h3>
                <Segmentado
                  opciones={[{ clave: "tx", etiqueta: "Volumen" }, { clave: "valor", etiqueta: "Monto" }]}
                  elegida={medida}
                  onElegir={setMedida}
                  etiqueta="Medida de los comparativos"
                />
              </div>
              <div className="grid gap-3 xl:grid-cols-2">
                <Comparativo
                  titulo={`${mesEnPalabras(datos.comparisons.months.current)} contra ${mesEnPalabras(datos.comparisons.months.previous)}`}
                  actual={mesEnPalabras(datos.comparisons.months.current)}
                  anterior={mesEnPalabras(datos.comparisons.months.previous)}
                  lineas={datos.comparisons.months.days}
                  medida={medida}
                  etiqueta={(dia) => String(dia)}
                  nombreDeLinea={(dia) => `día ${dia}`}
                />
                <Comparativo
                  titulo={`${datos.comparisons.years.current} contra ${datos.comparisons.years.previous}`}
                  actual={String(datos.comparisons.years.current)}
                  anterior={String(datos.comparisons.years.previous)}
                  lineas={datos.comparisons.years.months}
                  medida={medida}
                  etiqueta={(mes) => MESES_CORTOS[mes - 1]}
                  nombreDeLinea={(mes) => MESES_CORTOS[mes - 1]}
                />
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 xl:grid-cols-2">
            <Tarjeta titulo="Por tipo de transacción" subtitulo="Cantidad y valor en el periodo">
              <Tabla
                etiqueta="Por tipo de transacción"
                columnas={[{ titulo: "Transacción" }, { titulo: "TX", numerica: true }, { titulo: "Valor", numerica: true }]}
                vacia={datos.byConcept.length ? null : "Sin transacciones en el periodo"}
              >
                {datos.byConcept.map((c) => (
                  <tr key={c.concept} className={fila()}>
                    <td className={celda({ principal: true })}>{c.label}</td>
                    <td className={celda({ numerica: true })}>{formatMoney(c.tx)}</td>
                    <td className={celda({ numerica: true })}>{montoLegible(c.valor)}</td>
                  </tr>
                ))}
              </Tabla>
            </Tarjeta>
            <Tarjeta titulo="Por regional" subtitulo="Con sus reversas y la tasa por cada 1.000 transacciones">
              <TablaDeLugares
                etiqueta="Por regional"
                columna="Regional"
                filas={datos.byRegion}
                nombre={(r) => ({ clave: r.region, principal: r.region, detalle: `${r.offices} ${r.offices === 1 ? "oficina" : "oficinas"}` })}
              />
            </Tarjeta>
          </div>

          {(datos.byDepartment || []).length ? (
            <div className="grid gap-3 xl:grid-cols-2">
              <Tarjeta titulo="Por departamento" subtitulo="Según el departamento de cada oficina en el registro">
                <TablaDeLugares
                  etiqueta="Por departamento"
                  columna="Departamento"
                  filas={datos.byDepartment}
                  nombre={(d) => ({ clave: d.department, principal: d.department, detalle: `${d.offices} ${d.offices === 1 ? "oficina" : "oficinas"}` })}
                />
              </Tarjeta>
              <Tarjeta titulo="Por municipio" subtitulo="Según el código DANE de cada oficina">
                <TablaDeLugares
                  etiqueta="Por municipio"
                  columna="Municipio"
                  filas={datos.byMunicipality}
                  nombre={(m) => ({ clave: m.dane, principal: nombreDeMunicipio(m.dane), detalle: m.department })}
                />
              </Tarjeta>
            </div>
          ) : null}

          <Tarjeta titulo="Por oficina" subtitulo="Ordenadas por valor transado">
            <Tabla
              etiqueta="Por oficina"
              alto="max-h-[420px]"
              columnas={[
                { titulo: "Oficina" }, { titulo: "Regional" }, { titulo: "TX", numerica: true }, { titulo: "Valor", numerica: true },
                { titulo: "Reversas", numerica: true }, { titulo: "Valor reversas", numerica: true }, { titulo: "Por 1.000", numerica: true },
              ]}
              vacia={datos.byOffice.length ? null : "Sin transacciones en el periodo"}
            >
              {datos.byOffice.map((o) => (
                <tr key={o.officeCode} className={fila()}>
                  <td className={celda({ principal: true })}>
                    {o.officeName}
                    <span className="block text-[11.5px] font-normal tabular-nums text-brinks-muted-foreground">{o.officeCode}</span>
                  </td>
                  <td className={celda()}>{o.region}</td>
                  <td className={celda({ numerica: true })}>{formatMoney(o.tx)}</td>
                  <td className={celda({ numerica: true })}>{montoLegible(o.valor)}</td>
                  <td className={celda({ numerica: true })}>{o.reversals || "—"}</td>
                  <td className={celda({ numerica: true })}>{o.reversals ? montoLegible(o.reversalsValue) : "—"}</td>
                  <td className={celda({ numerica: true })}>{o.reversals ? tasa(o.rate) : "—"}</td>
                </tr>
              ))}
            </Tabla>
          </Tarjeta>

          <div className="grid gap-3 xl:grid-cols-3">
            <Tarjeta titulo={porDia ? "Reversas por día" : "Reversas por mes"} subtitulo="Cuántas transacciones reversó el banco" className="xl:col-span-2">
              <LugarDeGrafica vacio={sinReversas ? "Sin reversas en el periodo" : null} alto="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={puntos} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--brinks-border)" vertical={false} />
                    <XAxis dataKey="etiqueta" {...EJE} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis allowDecimals={false} {...EJE} width={32} />
                    <Tooltip cursor={{ fill: "var(--brinks-surface)" }} />
                    <Bar dataKey="reversas" name="Reversas" fill="var(--brinks-danger)" radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </LugarDeGrafica>
            </Tarjeta>
            <Tarjeta titulo="Motivo de la reversa"><Barras filas={datos.reversals.byMotive} /></Tarjeta>
          </div>

          <div className="grid gap-3 xl:grid-cols-3">
            <Tarjeta titulo="Atribución" subtitulo="A quién se le atribuye el error"><Barras filas={datos.reversals.byAttribution} /></Tarjeta>
            <Tarjeta titulo="Transacción reversada"><Barras filas={datos.reversals.byType} /></Tarjeta>
            <Tarjeta titulo="Cajeros con más reversas">
              {datos.reversals.byCashier.length ? (
                <ul className="max-h-[320px] space-y-2.5 overflow-y-auto pr-1">
                  {datos.reversals.byCashier.map((c) => (
                    <li key={c.key} className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block break-words text-[12.5px] font-medium text-brinks-foreground">{c.cashier}</span>
                        <span className="block text-[11.5px] text-brinks-muted-foreground">{c.officeName}</span>
                      </span>
                      <span className="shrink-0 text-right text-[12px] tabular-nums text-brinks-muted-foreground">
                        <span className="block font-medium text-brinks-foreground">{c.count}</span>
                        {montoLegible(c.value)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-6 text-center text-[12.5px] text-brinks-muted-foreground">Sin reversas en el periodo</p>}
            </Tarjeta>
          </div>

          <Tarjeta titulo="Últimas reversas" subtitulo="Las del archivo del banco, las registradas en los cierres y las del consolidado anterior">
            <Tabla
              etiqueta="Últimas reversas"
              alto="max-h-[420px]"
              columnas={[
                { titulo: "Fecha" }, { titulo: "Oficina" }, { titulo: "Cajero" }, { titulo: "Transacción" },
                { titulo: "Motivo" }, { titulo: "Atribución" }, { titulo: "Valor", numerica: true },
              ]}
              vacia={datos.reversals.latest.length ? null : "Sin reversas en el periodo"}
            >
              {datos.reversals.latest.map((r) => (
                <tr key={r.id} className={fila()}>
                  <td className={celda({ numerica: true, tono: "text-left" })}>{fechaDMY(r.transactionDate)}</td>
                  <td className={celda({ principal: true })}>{r.officeName}</td>
                  <td className={celda()}>{r.cashier || "—"}</td>
                  <td className={celda()}>{r.transactionType}</td>
                  <td className={celda({ tono: r.pending ? "text-brinks-warning" : undefined })}>{r.motive}</td>
                  <td className={celda({ tono: r.pending ? "text-brinks-warning" : undefined })}>{r.attribution}</td>
                  <td className={celda({ numerica: true })}>{montoLegible(r.amount)}</td>
                </tr>
              ))}
            </Tabla>
          </Tarjeta>
        </div>
      ) : null}
    </section>
  );
}
